import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

interface CropGridProps {
  title?: string;
  subtitle?: string;
  className?: string;
}

const CropGrid: React.FC<CropGridProps> = ({ 
  title = "Crop-Specific pH Guides",
  subtitle = "Click any crop to learn how sulfur burners can optimize your soil pH and boost yields",
  className = ""
}) => {
  const crops = [
    // Row 1
    { name: 'Almonds', emoji: '/Almond.png', ph: '6.0-7.0', link: '/almond', description: 'Unlock micronutrients in high-pH soils for stronger nut fill.' },
    { name: 'Apples', emoji: '🍎', ph: '6.0-6.5', link: '/apple', description: 'Improve calcium uptake for crisp texture and longer shelf life.' },
    { name: 'Apricots', emoji: '🍑', ph: '6.0-7.0', link: '/apricot', description: 'Prevent nutrient lockout and toxicity for premium stone fruit.' },
    { name: 'Artichokes', emoji: '/Artichoke.png', ph: '6.5-7.5', link: '/artichoke', description: 'Balance soil pH to boost bud quality and uniformity.' },
    { name: 'Asparagus', emoji: '/Asparagus.png', ph: '6.0-8.0', link: '/asparagus', description: 'Support deep root growth in compacted alkaline ground.' },
    { name: 'Avocados', emoji: '🥑', ph: '6.0-7.0', link: '/avocado', description: 'Prevent iron chlorosis and leaf drop in calcareous soils.' },
    { name: 'Beans', emoji: '🫘', ph: '6.0-7.0', link: '/bean', description: 'Maximize nitrogen-fixing efficiency with optimal root zone pH.' },
    { name: 'Beets', emoji: '/Beets Emoji.png', ph: '6.0-7.5', link: '/beet', description: 'Promote better color and sugar content in alkaline soils.' },
    { name: 'Blackberries', emoji: '/Blackberry Emoji.png', ph: '5.0-6.0', link: '/blackberry', description: 'Improve flavor and size by enhancing nutrient mobility.' },
    { name: 'Blueberries', emoji: '🫐', ph: '4.5-5.5', link: '/blueberry', description: 'Low pH = premium yield, deep color, and sweeter berries.' },
    
    // Row 2
    { name: 'Broccoli', emoji: '🥦', ph: '6.0-7.0', link: '/broccoli', description: 'Avoid boron lockout for tight, healthy heads.' },
    { name: 'Brussels Sprouts', emoji: '/Brussels Sprout.png', ph: '6.0-7.5', link: '/brussels-sprouts', description: 'Boost sulfur metabolism for flavor and firmness.' },
    { name: 'Cabbage', emoji: '/Cabbage.png', ph: '6.0-7.5', link: '/cabbage', description: 'Reduce tip burn and improve wrapper leaf development.' },
    { name: 'Cannabis', emoji: '/Cannibis Emoji.png', ph: '5.8-6.5', link: '/cannabis', description: 'Lower pH for max terpene expression and nutrient uptake.' },
    { name: 'Carrots', emoji: '🥕', ph: '5.5-7.0', link: '/carrot', description: 'Enhance phosphorus access for longer, smoother roots.' },
    { name: 'Cauliflower', emoji: '/Cauliflower Emoji.png', ph: '5.5-7.5', link: '/cauliflower', description: 'Combat bicarbonate stress to ensure tight, white curds.' },
    { name: 'Celery', emoji: '/Celery Emoji.png', ph: '5.8-7.0', link: '/celery', description: 'Prevent tip burn and hollow stems with improved Ca uptake.' },
    { name: 'Cherries', emoji: '🍒', ph: '6.0-7.0', link: '/cherry', description: 'Reduce fruit splitting by balancing micronutrient availability.' },
    { name: 'Citrus', emoji: '🍊', ph: '6.0-7.5', link: '/citrus', description: 'Unlock iron and zinc to brighten foliage and flavor.' },
    { name: 'Corn', emoji: '🌽', ph: '6.0-6.8', link: '/corn', description: 'Lower bicarbonates to enhance early vigor and stand uniformity.' },
    
    // Row 3
    { name: 'Cranberries', emoji: '/Cranberries.png', ph: '4.0-5.5', link: '/cranberry', description: 'Maintain ultra-low pH for root health and fruit set.' },
    { name: 'Cucumbers', emoji: '🥒', ph: '5.5-7.0', link: '/cucumber', description: 'Keep soil balanced to avoid bitter fruit and blossom end rot.' },
    { name: 'Eggplant', emoji: '🍆', ph: '5.5-6.5', link: '/eggplant', description: 'Boost potassium availability for firmer, glossier fruit.' },
    { name: 'Garlic', emoji: '🧄', ph: '6.0-7.0', link: '/garlic', description: 'Support healthy bulb formation by stabilizing pH at the roots.' },
    { name: 'Grapes', emoji: '🍇', ph: '5.5-6.5', link: '/grape', description: 'Eliminate chlorosis and boost Brix in arid vineyards.' },
    { name: 'Hops', emoji: '🍺', ph: '6.0-6.5', link: '/hops', description: 'Improve cone quality and aroma by optimizing nutrient flow.' },
    { name: 'Leeks', emoji: '/Leeks Emoji.png', ph: '6.0-8.0', link: '/leek', description: 'Deepen root development in tight, high-pH soils.' },
    { name: 'Lettuce', emoji: '🥬', ph: '6.0-7.0', link: '/lettuce', description: 'Prevent tip burn and nutrient lockout in drip systems.' },
    { name: 'Melons', emoji: '/Melon.png', ph: '6.0-7.0', link: '/melon', description: 'Boost sugar content and skin firmness with better Ca/Mg balance.' },
    { name: 'Onions', emoji: '🧅', ph: '6.0-7.0', link: '/onion', description: 'Improve sulfur availability for pungency and storage quality.' },
    
    // Row 4
    { name: 'Parsley', emoji: '/Parsley.png', ph: '5.0-7.0', link: '/parsley', description: 'Balance pH to ensure lush, green foliage and aromatic oils.' },
    { name: 'Peaches', emoji: '🍑', ph: '6.0-7.0', link: '/peach', description: 'Avoid zinc and boron deficiency in tree fruit zones.' },
    { name: 'Pears', emoji: '🍐', ph: '6.0-7.0', link: '/pear', description: 'Enhance calcium uptake for reduced internal breakdown.' },
    { name: 'Peas', emoji: '/Peas Emoji.png', ph: '6.0-7.5', link: '/pea', description: 'Support nodulation and growth in bicarbonate-prone beds.' },
    { name: 'Peppers', emoji: '🌶️', ph: '5.5-7.0', link: '/pepper', description: 'Reduce blossom end rot and boost capsaicin production.' },
    { name: 'Pistachios', emoji: '/Pistachio.png', ph: '6.0-7.5', link: '/pistachio', description: 'Correct salt buildup and unlock potassium in desert soils.' },
    { name: 'Plums', emoji: '🟣', ph: '6.0-7.0', link: '/plum', description: 'Strengthen tree vigor and yield in marginal ground.' },
    { name: 'Potatoes', emoji: '🥔', ph: '4.8-6.5', link: '/potato', description: 'Fight scab and boost tuber uniformity with better soil balance.' },
    { name: 'Pumpkins', emoji: '🎃', ph: '5.5-7.5', link: '/pumpkin', description: 'Improve stem strength and skin finish for better storage.' },
    { name: 'Radishes', emoji: '/Radish.png', ph: '6.0-7.0', link: '/radish', description: 'Speed maturity and size consistency with optimized uptake.' },
    
    // Row 5
    { name: 'Raspberries', emoji: '/Raspberry Emoji.png', ph: '5.5-6.5', link: '/raspberry', description: 'Sweeten berries and prevent cane dieback in alkaline zones.' },
    { name: 'Spinach', emoji: '/Spinach.png', ph: '6.0-7.5', link: '/spinach', description: 'Prevent chlorosis and boost leaf turgor with ideal root pH.' },
    { name: 'Squash', emoji: '/Squash.png', ph: '5.5-7.0', link: '/squash', description: 'Avoid uneven fruit development in stressed alkaline fields.' },
    { name: 'Strawberries', emoji: '🍓', ph: '5.5-6.5', link: '/strawberry', description: 'Enhance berry size, sugar, and shelf life in drip systems.' },
    { name: 'Sweet Potatoes', emoji: '🍠', ph: '5.8-6.2', link: '/sweet-potato', description: 'Improve nutrient density and skin smoothness.' },
    { name: 'Tomatoes', emoji: '🍅', ph: '6.0-6.8', link: '/tomato', description: 'Fight blossom end rot and unlock full flavor potential.' },
    { name: 'Turnips', emoji: '/Turnip Emoji.png', ph: '5.5-6.8', link: '/turnip', description: 'Prevent nutrient lockout and bitter roots in high-pH soils.' },
    { name: 'Walnuts', emoji: '/Walnut.png', ph: '6.0-7.5', link: '/walnut', description: 'Correct trace mineral imbalance for stronger shell set.' },
    { name: 'Watermelons', emoji: '🍉', ph: '5.8-7.0', link: '/watermelon', description: 'Enhance sweetness and cell structure in hot climates.' },
    { name: 'Zucchini', emoji: '🥒', ph: '6.0-7.5', link: '/zucchini', description: 'Improve flower retention and fruit shape under irrigation stress.' }
  ];

  // Check if crop has an existing page
  const hasExistingPage = (link: string) => {
    const existingPages = ['/almond', '/apple', '/apricot', '/artichoke', '/asparagus', '/blueberry', '/cannabis', '/cherry', '/grape', '/hops', '/pistachio', '/raspberry', '/tomato'];
    return existingPages.includes(link);
  };

  return (
    <section className={`py-16 bg-gradient-to-br from-green-50 to-emerald-50 ${className}`}>
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
            {title}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {subtitle}
          </p>
        </motion.div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-10 gap-3 md:gap-4">
          {crops.map((crop, index) => (
            <div
              key={crop.name}
              className="group cursor-pointer relative"
            >
              {hasExistingPage(crop.link) ? (
                <Link
                  to={crop.link}
                  className="block bg-white rounded-lg p-2 md:p-3 shadow-sm border border-green-200 hover:border-green-400 hover:shadow-md transition-all duration-200"
                >
                  <div className="text-center">
                    <div className="text-2xl md:text-3xl mb-2 group-hover:scale-110 transition-transform duration-200">
                      {crop.emoji.startsWith('/') ? (
                        <img 
                          src={crop.emoji} 
                          alt={crop.name} 
                          className="w-8 h-8 md:w-10 md:h-10 mx-auto object-contain"
                          onError={(e) => {
                            // Fallback to text emoji if image fails
                            e.currentTarget.style.display = 'none';
                            const fallback = document.createElement('div');
                            fallback.textContent = '🌱';
                            fallback.className = 'text-2xl md:text-3xl';
                            e.currentTarget.parentNode?.appendChild(fallback);
                          }}
                        />
                      ) : (
                        crop.emoji
                      )}
                    </div>
                    <h3 className="text-xs md:text-sm font-bold text-gray-900 mb-1 group-hover:text-green-600 transition-colors leading-tight line-clamp-1">
                      {crop.name}
                    </h3>
                    <div className="text-green-600 font-semibold text-xs mb-1">
                      {crop.ph}
                    </div>
                  </div>
                  
                  {/* Hover Tooltip */}
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 w-48 text-center">
                    <div className="font-semibold mb-1">{crop.name}</div>
                    <div className="text-gray-300">{crop.description}</div>
                    {/* Arrow pointing down */}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                  </div>
                </Link>
              ) : (
                <div className="block bg-white rounded-lg p-2 md:p-3 shadow-sm border border-gray-200 cursor-pointer relative">
                  <div className="text-center">
                    <div className="text-2xl md:text-3xl mb-2">
                      {crop.emoji.startsWith('/') ? (
                        <img 
                          src={crop.emoji} 
                          alt={crop.name} 
                          className="w-8 h-8 md:w-10 md:h-10 mx-auto object-contain"
                          onError={(e) => {
                            // Fallback to text emoji if image fails
                            e.currentTarget.style.display = 'none';
                            const fallback = document.createElement('div');
                            fallback.textContent = '🌱';
                            fallback.className = 'text-2xl md:text-3xl';
                            e.currentTarget.parentNode?.appendChild(fallback);
                          }}
                        />
                      ) : (
                        crop.emoji
                      )}
                    </div>
                    <h3 className="text-xs md:text-sm font-bold text-gray-900 mb-1 leading-tight line-clamp-1">
                      {crop.name}
                    </h3>
                    <div className="text-gray-500 font-semibold text-xs mb-1">
                      {crop.ph}
                    </div>
                  </div>
                  
                  {/* Hover Tooltip for disabled crops */}
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 w-48 text-center">
                    <div className="font-semibold mb-1">{crop.name}</div>
                    <div className="text-gray-300">{crop.description}</div>
                    <div className="text-yellow-300 mt-1 text-xs">Guide coming soon</div>
                    {/* Arrow pointing down */}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="text-center mt-12"
        >
          <div className="bg-white rounded-xl p-6 shadow-lg border border-green-200 max-w-3xl mx-auto">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Why pH Matters for Every Crop
            </h3>
            <p className="text-gray-700 leading-relaxed">
              Each crop has an optimal soil pH range where nutrients are most available. 
              When irrigation water contains high bicarbonates, it gradually raises soil pH over time, 
              potentially moving it outside your crop's preferred range. Our sulfur burners help 
              maintain the ideal pH by neutralizing bicarbonates in your irrigation water.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CropGrid;